<template>
  <div v-if="journeyOptions" class="ba-pane" :class="[`device--${selectedDevice}`]">
    <ba-pane-control v-show="journeyData && !noData"
      :paneScale="paneScaleForSlider"
      :ratioOption="ratioOption"
      :seeAll="seeAll"
      :pluck="settingPluck"
      :grouping="grouping"
      :group="group"
      :groupItems="displayGroupItems"
      :reportShowing="reportShowing"
      @onPaneScaleChange="paneScale = $event"
      @onPaneScaleFit="onPaneScaleFit"
      @onChangeRatioOption="setRatioOption"
      @onChangeSeeAll="setSeeAll"
      @onChangePluck="setPluck"
      @onChangeGrouping="setGrouping"
      @onGroupChange="onGroupChange"
      @onDownloadExcel="onDownloadExcel"
    />
    <transition name="pluck">
      <pluck class="pluck-pane"
            v-if="settingPluck"
            :activated="activated"
            :pluckError="pluckError"
            :disabled="noData"
            :tracking="trackingData"
            @sharing="onPluckSharing"
            @loading="onPluckLoading"
            @alerting="onPluckAlerting"
      />
    </transition>
    <div class="pane-wrap"
        :class="{ disabled: reportShowing, 'is-geometry-hidden': paneGeometryHidden }"
        ref="paneWrap"
        v-if="journeyData && !noData"
    >

      <div :style="{ 'min-width': paneWidth * paneScale + 'px', 'min-height': paneHeight * paneScale + 'px'}">
        <div class="pane"
            ref="pane"
            :class="{ dragging: paneDragging }"
            :style="{ 'width': paneWidth + 'px',
                      'height': paneHeight + 'px',
                      'margin-top': paneMarginTop - 500 + 'px',
                      'margin-left': paneMarginLeft - 500 + 'px',
                      'transform': `scale(${paneScale})`
                    }"
        >
          <div class="pane-back" @click="resetActive('click')"></div>

          <div class="nodes">
            <ba-page-node
              :key="loaded"
              :itemDistance="itemDistance"
              :active="false"
              :all="true"
              :index="{ x: 1, y: 1 }"
              :indexMax="indexMax"
              :indexShow="calcIndexShow"
              :hovered="hovered"
              :activated="activated"
              :selected="selected"
              :reportShowing="reportShowing"
              :captureProcessing="captureProcessing"
              :seeAll="seeAll"
              :pageData="journeyData"
              :ratioOption="ratioOption"
              :valueRange="valueRange"
              :defaultThumbnail="defaultThumbnail"
              :pluckTreeInfo="pluckTreeInfo"
              :isTrackable="isTrackable"
              :tracking="trackingData"
              :grouping="grouping"
              :group="group"
              :mostOccurred="mostOccurred"
              :mostOccurredValue="mostOccurredValue"
              :overallSessionData="overallSessionData"
              @onHeatmap="onHeatmapView"
              @onShowReport="showReport($event)"
              @onResetActive="resetActive"
              @onCapture="onCapture"
              @onMoreSibling="showMoreSibling"
              @onActivePageNode="setActive"
              @onHoverPageNode="setHover"
              @onSearchJourney="onSearchJourney"
              @onCustomTag="onCustomTag"
              @onShowTrackingLayer="showTrackingLayer"
              @setTracking="setTracking"
            />
          </div>
        </div>
      </div>
      <div class="pane-dimmed" v-if="isDisabled"></div>
    </div>
    <transition name="grouping">
      <grouping class="grouping-pane"
                v-if="grouping && journeyData && !noData"
                :group="group"
                :groupItems="displayGroupItems"
                :domain="domain"
                @onGroupChange="onGroupChange" />
    </transition>

    <transition name="fade">
      <div v-if="noData" class="pane-no-data">
        <i class="icon icon--alert"></i><br>
        <h3 v-html="$t('journey.noData.title')"></h3>
        <p v-if="!noResampleData" v-html="$t('journey.noData.description')"></p>
        <p v-else v-html="$t('journey.noData.noResampleDescription')"></p>
      </div>
    </transition>

<!--    <transition name="fade" v-if="!noData && journeyData">-->
<!--      <div class="reliability" v-if="isSampling">-->
<!--        <i class="icon icon&#45;&#45;alarm"></i>-->
<!--        <span v-if="!isSurvey">{{$t('resample.info', {percent: originJourneyData.reliability})}}</span>-->
<!--        <span v-else>{{$t('resample.survey', {percent: domain.samplingRate})}}</span>-->
<!--      </div>-->
<!--    </transition>-->

    <transition name="fade" v-if="!noData">
      <div class="pane-base-info" v-if="journeyData || samplingReliabilityText">
        <div class="sampling-reliability" v-if="samplingReliabilityText">
          <div class="icon-wrap">
            <i class="icon icon--bell-white size-16"></i>
          </div>
          <div class="message">{{ samplingReliabilityText }}</div>
        </div>
        <div class="pane-date" v-if="journeyData">
          {{ $t('journey.basedOn', { date: dataTimeString, timezone: timezone }) }}
        </div>
      </div>
    </transition>

    <transition name="node-report">
      <div v-if="reportShowing && openNodeReport" class="node-report">
        <ba-node-report
          :pageData="reportShowing"
          :defaultThumbnail="defaultThumbnail"
          :captureProcessing="captureProcessing"
          :originData="originJourneyData"
          :valueRange="valueRange"
          :timezoneName="domain.timezoneName"
          :isCustomNode="isCustomNode"
          :group="grouping ? group : false"
          :isTracking="Boolean(trackingData)"
          :tracking="trackingData"
          @targetHeatmapPv="targetHeatmapPv = $event"
          @onHeatmap="onHeatmapView"
          @onCapture="onCapture"
        />
        <button class="close-report"
                @click="hideReport">
          <i class="icon icon--close-arrow-right"></i>
        </button>
      </div>
    </transition>

    <transition name="fade">
      <dimmed v-if="trackingLayerShowing">
        <ba-tracking-layer :nodeData="trackingNodeData" @close="trackingLayerShowing=false" @setTracking="setTracking" />
      </dimmed>
    </transition>

    <transition name="fade">
      <div class="partiallyLoading" v-if="partiallyLoading || apiLoading">
        <ba-loading :applyBackground="true" :showLoadingText="true" />
      </div>
    </transition>
    <dimmed v-if="mostOccurredLoading">
      <MostOccurredLoading />
    </dimmed>
    <dimmed v-if="capturingPage">
      <ba-capture
        :pageData="capturingPage"
        :capturingDevice="capturingDevice"
        :wholeCaptureProcessing="wholeCaptureProcessing"
        @onWholeCaptureProcessingChange="wholeCaptureProcessing = $event"
        @onCaptureProcessing="onCaptureProcessing($event)"
        @onCloseLayer="capturingPage = null"
      />
    </dimmed>

    <dimmed v-if="pluckSharing">
      <div class="pluck-sharing-container">
        <pluck-sharing :treeId="pluckSharing" @onClose="onPluckSharing(false)" />
      </div>
    </dimmed>

    <dimmed v-if="pluckLoading">
      <div class="pluck-loading-container">
        <pluck-loading @onCancel="onPluckLoading(false)" />
      </div>
    </dimmed>

    <dimmed v-if="pluckAlerting">
      <div class="pluck-alert-container">
        <alert :title="pluckAlert.title"
              :description="pluckAlert.description"
              :submit="pluckAlert.submit"
              :cancel="pluckAlert.cancel"
              @onSubmit="pluckAlert.onSubmit"
              @onCancel="onPluckAlerting(false)"
      />
      </div>
    </dimmed>

    <dimmed v-if="movingGroup">
      <alert :title="$t('group.alert.enter.title')"
            :description="$t('group.alert.enter.description')"
            :cancel="$t('common.cancel')"
            :submit="$t('common.confirm')"
            @onSubmit="onSettingGroup"
            @onCancel="movingGroup = false"
      />
    </dimmed>

    <dimmed v-if="isHeatmapView && pageDataForHeatmap">
      <HeatMap
        :pageData="pageDataForHeatmap"
        @setHeatMapView="isHeatmapView = $event"
        :heatmapParams="heatmapParams"
        @changeHeatmapParams="heatmapParams = $event"
        :targetHeatmapPv="targetHeatmapPv"
        :targetDeviceCapture="targetDeviceCapture"
        :tracking="trackingData"
        @onCapture="onCaptureInHeatmap"
        @onCaptureProcessing="onCaptureProcessing($event)"
        :wholeCaptureProcessing="wholeCaptureProcessing"
        @onWholeCaptureProcessingChange="wholeCaptureProcessing = $event"
        @onTargetRefresh="onTargetRefresh"
      />
    </dimmed>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { format } from 'date-fns'

import {
  getS3Url,
  getS3CaptureDatetime,
  simpleUrl,
  calculateLogDateTime,
  thousand,
  getOffset, getDomainForView
} from '@/libs/utils'
import { EventBus } from '@/libs/event-bus'
import { getData, getReliabilityData } from '@/api/journey'
import { checkCaptureExist } from '@/api/capture'
import { getGroupList } from '@/api/group'
import { checkCapture } from '@/api/heatmap'
import Excel from '@/libs/excel-export'

import Alert from '@/components/ui/Alert'
import Dimmed from '@/components/ui/Dimmed'
import BaLoading from '@/components/ui/ba/Loading'
import BaNodeReport from '@/components/journey/NodeReport'
import BaPaneControl from '@/components/journey/PaneControl'
import BaPageNode from '@/components/journey/PageNode'
import BaCapture from '@/components/journey/Capture'
import BaTrackingLayer from '@/components/journey/TrackingLayer'

import Pluck from '@/components/journey/Pluck'
import PluckLoading from '@/components/journey/PluckLoading'
import PluckSharing from '@/components/journey/PluckSharing'
import MostOccurredLoading from '@/components/journey/MostOccurredLoading'

import Grouping from '@/components/journey/Grouping'

import HeatMap from '@/components/journey/heatmap/HeatMap'

import axios from 'axios'
import { normalizeHeatmapData } from '@/libs/heatmap-data-normalizer'
import { checkSampling } from '@/libs/sampling-helper'

const CancelToken = axios.CancelToken

export default {
  name: 'BaPane',
  components: {
    Alert,
    Dimmed,
    BaLoading,
    BaNodeReport,
    BaPaneControl,
    BaPageNode,
    BaCapture,
    BaTrackingLayer,
    Pluck,
    PluckLoading,
    PluckSharing,
    Grouping,
    HeatMap,
    MostOccurredLoading
  },
  watch: {
    noData (value) {
      if (!value) {
        setTimeout(() => {
          this.setPaneDraggable()
        }, 300)
      }
    },
    journeyPluck () {
      if (this.$route.query.pluck) {
        const newQuery = { ...this.$route.query }
        delete newQuery.pluck
        this.$router.replace({ query: newQuery })
      }
    },
    activated () {
      this.setJourneyOptions({
        ...this.journeyOptions,
        activatedId: this.activated[this.activated.length - 1] ? this.activated[this.activated.length - 1].id : null,
        seq: this.activeIndexForJourney.x,
        rank: this.activeIndexForJourney.y
      })
    },
    trackingData: {
      handler (newValue) {
        EventBus.$emit('tracking-state', newValue !== null)
      },
      immediate: true
    }
  },
  computed: {
    ...mapState([
      'userData',
      'journeyPeriod',
      'journeyOptions',
      'journeyPluck',
      'apiCancelSource',
      'domain',
      'originJourneyData',
      'basicInfo',
      'isSampling',
      'aiReportData'
    ]),
    isCustomNode () {
      return this.journeyOptions.isCustomNode
    },
    isSurvey () {
      return this.journeyOptions.survey
    },
    isTrackable () {
      const customUserId = this.domain.beuCustomUserId
      return customUserId && customUserId.length > 0
    },
    timezone () {
      return getOffset(this.domain.timezoneName)
    },
    sid () {
      return this.$route.query.sid
    },
    subSid () {
      return this.$route.query.subSid
    },
    firstPath () {
      return this.$route.query.path
    },
    device () {
      return this.journeyOptions.device
    },
    selectedDevice () {
      return this.device || 'all'
    },
    displayGroupItems () {
      const items = {}
      for (const [groupId, groupName] of Object.entries(this.groupItems)) {
        items[groupId] = groupName === 'AUTO' ? this.$t('group.automatic') : groupName
      }
      return items
    },
    convertedDevice () {
      if (this.capturingDevice === 'desktop') {
        return 1
      } else if (this.capturingDevice === 'tablet') {
        return 2
      } else {
        return 3
      }
    },
    cacheKey () {
      return `${this.dataOption.device}_${this.dataOption.start}_${this.dataOption.end}_${this.dataOption.sid}_
      ${this.dataOption.matchType}_${this.dataOption.exist}_${this.dataOption.refer}_${this.dataOption.limit}_
      ${JSON.stringify(this.dataOption.utm)}_${JSON.stringify(this.dataOption.survey)}_
      ${JSON.stringify(this.dataOption.aiRefererIds)}_
      ${this.dataOption.treeId}_${this.dataOption.groupId}_${this.dataOption.isCustomNode}_
      ${this.dataOption.selectedMembers}_${this.dataOption.excludedMembers}_${this.dataOption.memberDisplayMode}_
      ${this.dataOption.isAllMemberSelected}_${this.dataOption.isAllNonMemberSelected}_
      ${this.dataOption.memberCacheKey}_${this.dataOption.sessionCacheKey}`
    },
    dataTimeString () {
      if (this.journeyData && this.domain) {
        // const loadedAt = this.journeyData.loadedAt
        const loadedAt = calculateLogDateTime(this.domain)
        const formatText = this.$t('journey.basedOnFormat')
        return format(loadedAt, formatText)
      } else {
        return ''
      }
    },
    itemDistances () {
      return {
        desktop: {
          first: {
            x: 135,
            y: 215
          },
          x: 330,
          y: this.isTrackable ? 243 : 203
        },
        tablet: {
          first: {
            x: 135,
            y: 215
          },
          x: 330,
          y: this.isTrackable ? 282 : 242
        },
        phone: {
          first: {
            x: 135,
            y: 215
          },
          x: 330,
          y: this.isTrackable ? 275 : 235
        },
        all: {
          first: {
            x: 135,
            y: 215
          },
          x: 330,
          y: this.isTrackable ? 278 : 238
        }
      }
    },
    paneCenter () {
      return {
        x: -this.paneWidth / 2 - 475,
        y: -this.paneHeight / 2 - 520
      }
    },
    paneWidth () {
      return this.itemDistance.first.x + this.itemDistance.x * this.calcIndexShow.x - 15
    },
    paneHeight () {
      return this.itemDistance.first.y + this.itemDistance.y * this.calcIndexShow.y + 27
    },
    paneScaleForSlider () {
      return Math.round(this.paneScale * 100)
    },
    itemDistance () {
      return this.itemDistances[this.selectedDevice]
    },
    pluckTreeInfo () {
      if (this.journeyPluck) {
        const result = []
        const pluckTree = this.journeyPluck.treeId.split('|')
        pluckTree.forEach(el => {
          result.push(parseInt(el.split('-')[1]))
        })
        return result
      }
      return null
    },
    calcIndexShow () {
      const activeIndexX = this.activeIndexForJourney.x + 1
      if (this.journeyPluck) {
        const pluckTree = this.journeyPluck.treeId.split('|')
        let x = 0
        let y = 0
        pluckTree.forEach(el => {
          x = Math.max(x, parseInt(el.split('-')[1]))
          y++
        })
        return {
          x: x,
          y: y
        }
      }
      return {
        x: activeIndexX,
        y: Math.max(this.indexMax.y, 1)
      }
    },
    trackingGetDataMode () {
      if (!this.isTrackable) return null
      if (this.trackingData) return 'count'
      return 'portion'
    },
    trackingDataOption () {
      if (!this.isTrackable) return {}
      if (!this.trackingData) return { isMemberDisplayed: true }

      const { type, isFromFunnel = false, isAllSelected, excludedMembers = [], selectedMembers = [], memberCacheKey, sessionCacheKey } = this.trackingData
      const data = {
        isMemberDisplayed: true
      }

      if (type === 'member') {
        data.memberCacheKey = memberCacheKey
        data.isAllMemberSelected = isAllSelected && isFromFunnel
        data.excludedMembers = excludedMembers.map(item => item.id)
        data.selectedMembers = isAllSelected ? [] : selectedMembers.map(item => item.id)
      } else {
        data.isAllNonMemberSelected = isAllSelected
        data.sessionCacheKey = sessionCacheKey
      }

      return data
    },
    dataOption () {
      return {
        device: this.journeyOptions.device,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        sid: this.journeyOptions.sid,
        subSid: this.journeyOptions.subSid,
        firstPathname: this.journeyOptions.firstPath,
        matchType: this.journeyOptions.matchType,
        exist: this.journeyOptions.exist,
        refer: this.journeyOptions.refer,
        aiRefererIds: this.journeyOptions.aiRefererIds,
        utm: this.journeyOptions.utm,
        survey: this.journeyOptions.survey,
        limit: this.journeyOptions.listSize.x,
        treeId: this.journeyPluck ? this.journeyPluck.treeId : null,
        isCustomNode: this.journeyOptions.isCustomNode,
        ...this.trackingDataOption
      }
    },
    activeIndexForJourney () {
      const maxRank = this.activated.map(item => {
        if (item && item.children && item.children.length) {
          return item.children.length
        } else {
          return 1
        }
      })

      return {
        x: this.activated.filter(node => node !== undefined).length - 1,
        y: Math.max(...maxRank)
      }
    },
    categoryLegendLink () {
      switch (this.$i18n.locale) {
        case 'ko':
          return 'https://forum.beusable.net/ko/solomon/post/2136'
        case 'ja':
          return 'https://forum.beusable.net/ja/solomon/post/2221'
        case 'en':
          return 'https://forum.beusable.net/en/solomon/post/2220'
      }
      return 'https://forum.beusable.net/ko/solomon/post/2136'
    },
    trackingBannerText () {
      if (!this.trackingData) return
      if (this.trackingData.type === 'guest') return this.$t('tracking.trackingBanner.guest')
      return this.trackingData.memberList.length === 1
        ? this.$t('tracking.trackingBanner.single', { data: this.trackingData.memberList[0].displayId })
        : this.$t('tracking.trackingBanner.multiple', { data: thousand(this.journeyData.memberCnt) })
    }
  },
  mounted () {
    this.getReliability()
    this.loaded = false

    EventBus.$on('paneDisabled', (value) => {
      this.isDisabled = value
    })

    EventBus.$on('onLoading', (value) => {
      if (this.pluckLoading && !value) {
        this.pluckLoading = false
      }
    })

    EventBus.$on('cancelLoading', (value) => {
      this.cancelLoading = value
    })

    EventBus.$onSticky('loadPaneData', () => {
      this.loadData()
      this.getReliability()
      EventBus.$clearSticky('loadPaneData')
    })
    EventBus.$onSticky('loadPaneNoData', (value, noResampleData) => {
      this.initMostOccurred()
      this.hideReport()
      EventBus.$emit('onLoading', false)
      this.noData = value
      if (noResampleData !== undefined) {
        this.noResampleData = noResampleData
      } else {
        this.noResampleData = false
      }
      this.setGlobalNoData(this.noData)
      EventBus.$clearSticky('loadPaneNoData')
    })

    EventBus.$on('journeyOptionChange', () => {
      this.setJourneyPluck(null)
      this.trackingData = null
    })

    EventBus.$on('settingGroup', (value = true) => {
      this.movingGroup = value
    })

    EventBus.$on('updateBookmark', (gUrlId, value) => {
      this.updateBookmark(this.journeyData, gUrlId, value)
    })

    EventBus.$on('updateBaApply', (value) => {
      this.updateBaApply(this.journeyData, value.gUrlId, value)
    })

    this.captureProcessingTimer = setInterval(() => {
      const data = this.captureProcessing
      if (data) {
        checkCaptureExist(data.sid, data.urlId, data.matchType, data.url)
          .then(result => {
            if (result.imagePath && result.imagePath[this.capturingDevice]) {
              let findByKeyAndReplaceCaptureData = (data, pageData, captureData) => {
                if (pageData) {
                  const deviceCapture = result.imagePath[this.capturingDevice]
                  const newCaptureDate = getS3CaptureDatetime(deviceCapture) || result.captureDate

                  if (deviceCapture && pageData.realCaptureDate[this.capturingDevice] !== newCaptureDate) {
                    if (pageData.key === data.key) {
                      // this.$set(this.captureProcessing, data, false)
                      this.captureProcessing = null
                      pageData.captureDate = result.captureDate
                      pageData.realCaptureDate[this.capturingDevice] = newCaptureDate
                      pageData.imagePath[this.capturingDevice] = captureData.imagePath[this.capturingDevice]
                      pageData.realCaptureUrl[this.capturingDevice] = captureData.realCaptureUrl[this.capturingDevice]

                      const currentThumbnail = this.getThumbnailPath(pageData, this.device)
                      if (currentThumbnail) {
                        pageData.thumbnail = getS3Url(currentThumbnail)
                      }
                    }
                    if (pageData.children && pageData.children.length) {
                      for (let i = 0; i < pageData.children.length; i++) {
                        findByKeyAndReplaceCaptureData(data, pageData.children[i], captureData)
                      }
                    }
                  }
                }
              }
              findByKeyAndReplaceCaptureData(data, this.journeyData, result)
            }
          })
          .catch(() => {
            // this.$set(this.captureProcessing, data, false)
            this.captureProcessing = null
          })
      }
    }, 5000)

    this.wholeCaptureProcessingTimer = setInterval(() => {
      const data = this.wholeCaptureProcessing
      if (data.length !== 0) {
        for (let index in data) {
          if (this.wholeCaptureProcessing[index].state !== 'success') {
            const params = `/${data[index].requestId}/${data[index].uuid}`
            checkCapture(params, true).then(task => {
              const tasks = task.data
              if (!tasks.length) {
                throw new Error('no tasks')
              }
              let successCount = 0
              let failedCount = 0
              for (const item of tasks) {
                if (item.statusCode === 10) {
                  successCount++
                } else if (item.statusCode < 0) {
                  failedCount++
                }
                if (item.statusCode === 10 && item.deviceCode === this.convertedDevice) {
                  this.targetDeviceCapture = true
                }
                if (successCount + failedCount === tasks.length) {
                  if (successCount === 0) {
                    this.wholeCaptureProcessing[index].state = 'failed'
                    let failedCheck = true
                    this.wholeCaptureProcessing.forEach(item => {
                      if (item.state !== 'failed') {
                        failedCheck = false
                      }
                    })
                    if (failedCheck) {
                      this.wholeCaptureProcessing[index].state = 'failed'
                    }
                  } else {
                    this.wholeCaptureProcessing[index].state = 'success'
                    let successCheck = true
                    this.wholeCaptureProcessing.forEach(item => {
                      if (item.state !== 'success') {
                        successCheck = false
                      }
                    })
                    if (successCheck) {
                      this.wholeCaptureProcessing[index].state = 'success'
                    }
                  }
                }
              }
            }).catch((err) => {
              console.log(err)
            })
          } else {
            console.log('processing')
          }
        }
      }
    }, 5000)

    if (this.journeyPluck) {
      this.setPluck(true)
    }

    this.loadGroupList()
  },
  beforeDestroy () {
    clearInterval(this.captureProcessingTimer)
    clearInterval(this.wholeCaptureProcessingTimer)
    this.captureProcessingTimer = null
    this.wholeCaptureProcessingTimer = null
    EventBus.$off('loadPaneData')
    EventBus.$off('loadPaneNoData')
  },
  methods: {
    ...mapMutations([
      'setJourneyPluck',
      'setApiCancelSource',
      'setOriginJourneyData',
      'setAIReportData',
      'setGlobalNoData',
      'setJourneyOptions'
    ]),
    onCustomTag () {
      this.setJourneyOptions({
        ...this.journeyOptions,
        isCustomNode: !this.journeyOptions.isCustomNode
      })

      EventBus.$emitSticky('loadPaneData')
    },
    getReliability () {
      if (!(this.domain.preSamplingRate < 100 || this.domain.postSamplingRate < 100)) return

      const requestData = {
        sid: this.journeyOptions.sid,
        matchType: this.journeyOptions.matchType,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end
      }

      if (new Date(this.journeyPeriod.start).getTime() >= new Date(this.domain.samplingChangeDate).getTime()) {
        getReliabilityData(requestData).then(res => {
          const result = this.$t('common.samplingReliability.reliabilityRateText', { data: res.reliabilityPercentage.toFixed(1) })
          this.samplingReliabilityText = result
        })
      } else {
        this.samplingReliabilityText = this.$t('common.samplingReliability.reliabilityRateException')
      }
    },
    resetAIReportData () {
      setTimeout(() => {
        if (this.aiReportData && this.aiReportData.treeId) {
          this.setJourneyPluck(null)
        }
        this.setAIReportData(null)
        if (this.$route.query.aiReportId) {
          const newQuery = { ...this.$route.query }
          delete newQuery.aiReportId
          delete newQuery.aiReportTreeId
          this.$router.replace({ query: newQuery })
        }
      }, 1000)
    },
    // initial most occurred
    initMostOccurred () {
      this.mostOccurred = false
      this.mostOccurredValue = null
      this.mostOccurredLoading = false
      this.setPluck(false)
    },
    applyJourneyNodeMeta (tree, nodeMap = this.nodeMap) {
      const meta = nodeMap[tree.key]
      const mergedNode = meta
        ? { ...tree, ...meta }
        : { ...tree }

      if (tree.children) {
        mergedNode.children = tree.children.map((child) => {
          return this.applyJourneyNodeMeta(child, nodeMap)
        })
      } else {
        mergedNode.children = tree.children
      }

      return mergedNode
    },
    saveJourneyCache (journeyData) {
      this.journeyDataCache[this.cacheKey] = {
        tree: journeyData,
        coreJourneyRankPath: this.coreJourneyRankPath
      }
    },
    loadedMostOccurred (maxNode) {
      this.activated = this.activated.slice(0, 4)

      if (this.journeyData.children) {
        this.journeyData.children = this.journeyData.children.slice(0, 3)
      }

      this.mostOccurred = true
      this.mostOccurredLoading = false
      this.setPluck(true)
      this.onPaneScaleFit()
    },
    onSearchMaxNodeBySeq (findSeq, exceptY) {
      let lastNode = null
      const journeyChildren = this.journeyData.children || []
      if (journeyChildren.length > 0 && findSeq >= 1) {
        for (let i = 0; i < journeyChildren.length; i++) {
          if (findSeq === 1) {
            if ((exceptY - 1) === i) {
              continue
            }
          }

          const seq1 = journeyChildren[i]

          if (seq1) {
            const seq1Children = seq1.children || []
            if (seq1Children.length > 0 && findSeq >= 2) {
              for (let j = 0; j < seq1Children.length; j++) {
                if ((exceptY - 1) === j) {
                  continue
                }
                const seq2 = seq1Children[j]

                if (seq2) {
                  const seq2Children = seq2.children || []
                  if (seq2Children.length > 0 && findSeq >= 3) {
                    for (let k = 0; k < seq2Children.length; k++) {
                      const seq3 = seq2Children[k]

                      if (seq3) {
                        if (!lastNode) {
                          lastNode = seq3
                        } else {
                          if (lastNode.sessCnt < seq3.sessCnt) {
                            lastNode = seq3
                          }
                        }
                      }
                    }
                  }
                }

                if (findSeq === 2) {
                  if (!lastNode) {
                    lastNode = seq2
                  } else {
                    if (seq2 && lastNode.sessCnt < seq2.sessCnt) {
                      lastNode = seq2
                    }
                  }
                }
              }
            }
          }

          if (findSeq === 1) {
            if (!lastNode) {
              lastNode = seq1
            } else {
              if (seq1 && lastNode.sessCnt < seq1.sessCnt) {
                lastNode = seq1
              }
            }
          }
        }
      }

      return lastNode
    },
    async onSearchJourney () {
      if (this.mostOccurred) {
        this.initMostOccurred()
        return
      }

      this.mostOccurredLoading = true
      this.mostOccurredValue = this.coreJourneyRankPath.join(', ')

      const pathNodes = []
      let currentNode = this.journeyData
      for (const rank of this.coreJourneyRankPath) {
        currentNode = currentNode.children[rank - 1]
        pathNodes.push(currentNode)
      }

      this.resetActive()

      pathNodes.slice(0, -1).forEach((node) => {
        node.children = node.children.slice(0, 3)
      })

      pathNodes.forEach((node) => {
        this.$set(this.activated, node.seq, node)
      })
      this.selected = pathNodes[pathNodes.length - 1]
      this.makeDisplayData(this.journeyData)
      this.loadedMostOccurred(this.selected)
    },
    getDomainName () {
      const subSid = this.journeyOptions.subSid
      let domainName = ''
      if (subSid) {
        this.domain.subDomains.forEach(domain => {
          if (!domainName && subSid === domain.orgSid) {
            domainName = domain.domain
          }
        })
      } else {
        domainName = this.domain.domain
      }

      return domainName
    },
    onTargetRefresh () {
      this.isHeatmapView = false
      this.reportShowing = false
      this.loadData()
    },
    onHeatmapView (pageData) {
      this.pageDataForHeatmap = null
      if (pageData) {
        pageData = normalizeHeatmapData(pageData)
      }
      const pageDataForHeatmap = pageData

      if (!this.isHeatmapView && pageDataForHeatmap) {
        this.pageDataForHeatmap = pageDataForHeatmap

        this.heatmapParams = null

        this.gUrlListForHeatmap = this.findNodeListById(null, pageData.id)

        this.heatmapParams = {
          serviceType: 'ba',
          // ...this.journeyOptions,
          // ...this.journeyPeriod,
          // ...this.reportShowing,
          parentNodeList: this.gUrlListForHeatmap,
          gUrlId: pageDataForHeatmap.gUrlId,
          parentSeq: pageDataForHeatmap.seq - 1,
          parentSid: pageDataForHeatmap.baApply.parentSid,
          sid: pageDataForHeatmap.baApply.sid,
          url: pageDataForHeatmap.url
        }
        // console.log('heatmap params : ', JSON.stringify(this.heatmapParams))

        this.isHeatmapView = true
      }
    },
    getPluckChildIndex (node, seq) {
      if (!this.journeyPluck) {
        return 0
      }

      const targetRank = this.pluckTreeInfo[seq]
      if (!node.children || !node.children.length) {
        return -1
      }

      const targetIndex = targetRank - 1
      if (node.children[targetIndex]) {
        return targetIndex
      }

      return 0
    },
    renderDataResult (result, oldData, isCancel) {
      if (!this.cancelLoading) {
        const tree = result.tree
        let showData = true
        let setPluckSelected = true
        if (this.pluckTreeInfo) {
          let currentNode = tree
          for (let i = 1; i < this.pluckTreeInfo.length; i++) {
            const rank = this.pluckTreeInfo[i]
            const children = currentNode && currentNode.children ? currentNode.children : []
            const nextNode = children[rank - 1]

            if (!nextNode) {
              if (i === 1) {
                showData = false
                const lastRank = children.length
                this.pluckError = 'pluck.changeFrom1'
                this.onPluckAlerting(true, {
                  title: this.$t('journey.pluck.noData.title'),
                  description: this.$t('journey.pluck.noData.description', { rank: lastRank }),
                  cancel: this.$t('common.confirm')
                })
                this.activated = [...oldData.activated]
                this.selected = { ...oldData.selected }
                this.paneGeometryHidden = false
                this.loaded = true
                EventBus.$emit('onLoading', false)
              } else {
                setPluckSelected = false
                this.onPluckAlerting(true, {
                  title: this.$t('journey.pluck.someData.title'),
                  description: this.$t('journey.pluck.someData.description', { seq: i - 1, seqNext: i }),
                  cancel: this.$t('common.confirm')
                })
              }
              break
            }

            currentNode = nextNode
          }
        }

        if (showData) {
          if (this.isSampling && !this.isSurvey) {
            const samplingInfo = checkSampling(this.domain, this.basicInfo, this.originJourneyData, tree)
            if (samplingInfo) {
              this.setOriginJourneyData(samplingInfo)
            } else {
              EventBus.$emitSticky('loadPaneNoData', true, true)
            }

            // const sessionSamplingRate = this.originJourneyData.sessionSamplingRate
            tree.resampleDropCnt = tree.dropCnt / samplingInfo.sessionSamplingRate
            tree.resampleCnt = tree.cnt / samplingInfo.sessionSamplingRate
            tree.resampleSessCnt = tree.totalSessCount
          }

          this.nodeMap = { ...result.nodeMap }
          this.coreJourneyRankPath = result.coreJourneyRankPath
          this.journeyData = this.applyJourneyNodeMeta({ ...tree }, this.nodeMap)
          if (this.journeyData && this.journeyData.cnt > 0) {
            if (!this.trackingData) {
              this.overallSessionData = {
                sessCnt: this.journeyData.sessCnt,
                memberSessCnt: this.journeyData.memberSessCnt
              }
            }
            this.journeyData.loadedAt = new Date()
            this.saveJourneyCache(this.journeyData)
            this.setActive(0, this.journeyData, false, false)
            this.makeData(this.journeyData)
            if (this.pluckTreeInfo) {
              let lastNode = this.journeyData
              for (let i = 1; i < this.pluckTreeInfo.length; i++) {
                const nextIndex = this.getPluckChildIndex(lastNode, i)
                if (nextIndex < 0) {
                  break
                }
                lastNode = lastNode.children[nextIndex]
              }
              this.setActive(this.journeyPluck.treeId.length, lastNode, false, setPluckSelected)
            }
            this.loaded = true
            if (this.pluckToBeSharing) {
              this.pluckSharing = this.pluckToBeSharing
            }
          } else {
            EventBus.$emitSticky('loadPaneNoData', true, this.isSampling && !this.isSurvey)
          }
        }
      } else {
        this.activated = [...oldData.activated]
        this.selected = { ...oldData.selected }
        this.loaded = true
        EventBus.$emit('onLoading', false)
      }
      this.apiLoading = false
      this.pluckLoading = false
      this.pluckToBeSharing = false

      setTimeout(() => {
        this.samplingInfo = true
      }, 500)
    },
    loadData () {
      const oldData = {
        activated: [...this.activated],
        selected: { ...this.selected }
      }

      if (this.mostOccurred) this.initMostOccurred()

      this.cancelLoading = false
      this.loaded = false
      this.paneGeometryHidden = Boolean(this.journeyPluck)
      this.activated = [null, null, null, null, null, null, null, null, null, null]
      this.hovered = null
      this.selected = null
      this.reportShowing = null
      this.pluckError = null
      this.samplingInfo = false
      this.nodeMap = {}
      this.coreJourneyRankPath = null

      if (!this.journeyPluck) {
        EventBus.$emit('onLoading', true)
      }

      setTimeout(() => {
        if (!this.journeyPluck && this.journeyDataCache[this.cacheKey]) {
          const cachedData = this.journeyDataCache[this.cacheKey]
          this.coreJourneyRankPath = cachedData.coreJourneyRankPath || null
          this.journeyData = {}
          Object.assign(this.journeyData, cachedData.tree)
          if (this.journeyData && this.journeyData.cnt > 0) {
            if (!this.trackingData) {
              this.overallSessionData = {
                sessCnt: this.journeyData.sessCnt,
                memberSessCnt: this.journeyData.memberSessCnt
              }
            }
            this.setActive(0, this.journeyData, false, false)
            this.makeData(this.journeyData)

            setTimeout(() => {
              if (this.pluckToBeSharing) {
                this.pluckSharing = this.pluckToBeSharing
                this.pluckToBeSharing = false
              }
            }, 600)
          } else {
            EventBus.$emitSticky('loadPaneNoData', true)
          }
        } else {
          this.apiLoading = true
          const dataOption = { ...this.dataOption }
          dataOption.domain = this.getDomainName()
          if (this.pluckTreeInfo) {
            dataOption.isPluck = true
            delete dataOption.limit
            this.pluckLoading = true
          } else {
            this.setJourneyPluck(null)
          }

          this.setApiCancelSource(CancelToken.source())
          getData(dataOption, true, this.apiCancelSource.token)
            .then(result => {
              this.resetAIReportData()
              this.renderDataResult(result, oldData)
            })
            .catch((e) => {
              if (this.journeyData && this.journeyData.loadedAt) {
                this.renderDataResult(this.journeyData, oldData, true)
              }

              EventBus.$emit('onError')
              this.apiLoading = false
              this.pluckLoading = false
              this.pluckToBeSharing = false
            })
        }
      }, 300)
    },
    makeData (pageData) {
      this.loaded = false

      pageData = normalizeHeatmapData(pageData)

      if (pageData.seq === 0) {
        this.$set(this.indexMax, 'x', 0)
        this.$set(this.indexMax, 'y', 0)
        this.valueRange.sessionTime = []
      }

      if (!pageData.calculated) {
        pageData.sessTime = Math.round(pageData.sessTime / 1000)

        // resample
        if (this.isSampling && this.originJourneyData && this.originJourneyData.sessionSamplingRate && pageData.key !== '-1') {
          let rate = this.originJourneyData.sessionSamplingRate
          pageData.resampleDropCnt = pageData.dropCnt / rate
          pageData.resampleCnt = pageData.cnt / rate
          pageData.resampleSessCnt = pageData.sessCnt / rate
        }

        pageData.linkUrl = simpleUrl(pageData.url, this.journeyOptions.matchType)
        pageData.calculated = true
      }

      pageData.imagePath = pageData.imagePath || {}
      pageData.realCaptureDate = {
        desktop: getS3CaptureDatetime(pageData.imagePath['desktop']) || pageData.captureDate,
        tablet: getS3CaptureDatetime(pageData.imagePath['tablet']) || pageData.captureDate,
        phone: getS3CaptureDatetime(pageData.imagePath['phone']) || pageData.captureDate
      }
      if (this.getThumbnailPath(pageData, this.device)) {
        const deviceCapture = this.getThumbnailPath(pageData, this.device)
        pageData.thumbnail = getS3Url(deviceCapture)
      }

      if (!this.defaultThumbnail && pageData.thumbnail) {
        this.defaultThumbnail = pageData.thumbnail
      }

      if (pageData.children && pageData.children.length) {
        pageData.children.forEach((child, idx) => {
          child.index = {
            x: child.seq + 1,
            y: idx + 1
          }
          this.$set(this.indexMax, 'x', Math.max(this.indexMax.x, child.index.x))
          this.$set(this.indexMax, 'y', Math.max(this.indexMax.y, child.index.y))
          child.repeat = child.reloadCnt ? child.reloadCnt / child.sessCnt * 100 : 0
          this.makeData(child)
        })
      }

      if (pageData.seq === 0) {
        this.loaded = new Date().getTime()
        this.makeDisplayData(pageData)
        setTimeout(() => {
          this.setPaneDraggable()
        }, 300)
      }
    },
    getThumbnailPath (data, device) {
      let path = null
      if (data.imagePath) {
        const { imagePath } = data
        path = imagePath[device] || imagePath.desktop || imagePath.phone || imagePath.tablet
      }
      return path
    },
    loadGroupList () {
      return new Promise(resolve => {
        getGroupList({
          sid: this.sid
        }, true).then(result => {
          this.groupItems = {}
          for (let group of result) {
            if (group.usableStatus !== 'PENDING') {
              this.groupItems[group.groupId] = group.groupName
            }
          }
          resolve()
        })
      })
    },
    setPaneDraggable () {
      this.loaded = false
      this.paneDragging = true

      this.paneMarginTop = -this.paneHeight / 2
      this.paneMarginLeft = -this.paneWidth / 2

      const pane = this.$refs.pane
      const paneWrap = this.$refs.paneWrap

      let pos1 = 0
      let pos2 = 0
      let pos3 = 0
      let pos4 = 0
      let velocityX = 0
      let velocityY = 0

      let limitPosition = () => {
        if (paneWrap) {
          const paneWrapWidth = paneWrap.clientWidth
          const paneWrapHeight = paneWrap.clientHeight
          const paneWidth = this.paneWidth + 1000
          const paneHeight = this.paneHeight + 1000
          const scaleWidth = paneWidth / 2 * (1 - this.paneScale)
          const scaleHeight = paneHeight / 2 * (1 - this.paneScale)

          let limit = {
            top: (-paneWrapHeight / 2 - 1 + 500) - scaleHeight,
            bottom: (-(paneHeight - paneWrapHeight) - paneWrapHeight / 2 - 1 - 500) + scaleHeight,
            left: (-paneWrapWidth / 2 - 1 + 500) - scaleWidth,
            right: (-(paneWidth - paneWrapWidth) - paneWrapWidth / 2 - 1 - 500) + scaleWidth
          }

          if (paneWrapHeight < paneHeight * this.paneScale) {
            if (this.paneMarginTop - limit.top > 0) {
              this.paneMarginTop = limit.top
            }
            if (this.paneMarginTop - limit.bottom < 0) {
              this.paneMarginTop = limit.bottom
            }
          } else {
            this.paneMarginTop = this.paneCenter.y
          }
          if (paneWrapWidth < paneWidth * this.paneScale) {
            if (this.paneMarginLeft - limit.left > 0) {
              this.paneMarginLeft = limit.left
            }
            if (this.paneMarginLeft - limit.right < 0) {
              this.paneMarginLeft = limit.right
            }
          } else {
            this.paneMarginLeft = this.paneCenter.x
          }
        }
      }

      let dragMouseDown = (e) => {
        function composedPath (el) {
          let paths = []
          while (el) {
            paths.push(el)
            if (el.tagName === 'HTML') {
              paths.push(document)
              paths.push(window)
              return paths
            }
            el = el.parentElement
          }
        }

        e = e || window.event
        const path = e.path || (e.composedPath && e.composedPath()) || composedPath(e.target)

        for (const ele of path) {
          if (typeof ele.className === 'string') {
            if (ele.className.substring(0, 12) === 'ba-page-node' && ele.className !== 'ba-page-node-arrow' && ele.id !== 'nodeAll') {
              return
            }
            if (ele.classList.contains('targeting-button')) {
              return
            }
          }
        }
        if (e.type === 'touchstart') {
          e.preventDefault()
          pos3 = e.touches[0].clientX
          pos4 = e.touches[0].clientY
          this.paneDragging = true
          document.ontouchend = closeDragElement
          document.ontouchmove = elementDrag
        } else if (e.buttons === 1) {
          e.preventDefault()
          pos3 = e.clientX
          pos4 = e.clientY
          this.paneDragging = true
          document.onmouseup = closeDragElement
          document.onmousemove = elementDrag
        }
      }

      let elementDrag = (e) => {
        e = e || window.event
        if (e.type === 'touchmove') {
          pos1 = pos3 - e.touches[0].clientX
          pos2 = pos4 - e.touches[0].clientY
          pos3 = e.touches[0].clientX
          pos4 = e.touches[0].clientY
        } else {
          e.preventDefault()
          pos1 = pos3 - e.clientX
          pos2 = pos4 - e.clientY
          pos3 = e.clientX
          pos4 = e.clientY
        }

        velocityX = pos1
        velocityY = pos2
        this.paneMarginTop -= pos2
        this.paneMarginLeft -= pos1
      }

      let closeDragElement = () => {
        this.paneMarginTop -= velocityY * 1.5
        this.paneMarginLeft -= velocityX * 1.5
        document.onmouseup = null
        document.ontouchend = null
        document.onmousemove = null
        document.ontouchmove = null
        this.paneDragging = false
        limitPosition()
      }

      let isScrolling = false
      let detectingTimer = null
      let detectScrolling = () => {
        if (!isScrolling) {
          clearInterval(detectingTimer)
          this.paneDragging = false
          limitPosition()
        }
      }
      let scrollMouseWheel = (e) => {
        e = e || window.event
        e.preventDefault()
        if (!isScrolling) {
          clearInterval(detectingTimer)
          detectingTimer = setInterval(detectScrolling, 100)
        }
        isScrolling = true
        this.paneDragging = true
        this.paneMarginTop -= (e.deltaY || e.wheelDeltaY) / 2
        this.paneMarginLeft -= (e.deltaX || e.wheelDeltaX) / 2
        setTimeout(() => {
          if (isScrolling) {
            isScrolling = false
          }
        }, 100)
      }

      if (pane) {
        pane.onmousedown = dragMouseDown
        pane.ontouchstart = dragMouseDown
      }
      if (paneWrap) {
        paneWrap.onmousewheel = scrollMouseWheel
      }

      this.onPaneScaleFit()
      setTimeout(() => {
        this.paneDragging = false
        EventBus.$emitSticky('loadPaneNoData', false)
        EventBus.$emit('onLoading', false)
        setTimeout(() => {
          this.onPaneScaleFit()
          setTimeout(() => {
            this.paneGeometryHidden = false
          }, 120)
        }, 50)
      }, 300)
    },
    makeDisplayData (pageData) {
      this.loaded = false
      this.partiallyLoading = true

      if (pageData.seq === 0) {
        this.valueRange.rollback = []
        this.valueRange.dropOff = []
        this.valueRange.transition = []
        this.valueRange.sessionTime = []
        this.valueRange.repeat = []

        this.shownKey = []
        if (this.activated[0].key !== 'NONE|-1') {
          this.shownKey.push(this.activated[0].key)
        }

        let forceDisplaySubdomain = false
        for (let pageData of this.activated) {
          if (pageData && pageData.children) {
            for (let child of pageData.children) {
              if (child.url) {
                const subDomainEl = document.createElement('a')
                subDomainEl.href = child.url
                let subDomain = ''
                if (subDomainEl.hostname !== this.journeyOptions.domain) {
                  subDomain = subDomainEl.hostname.replace('.' + this.journeyOptions.domain, '')
                }
                const splitedSubDomain = subDomain.split('.')
                if (splitedSubDomain.length > 0 && splitedSubDomain[0] !== 'www' && splitedSubDomain[0].length > 0) {
                  forceDisplaySubdomain = true
                  break
                }
              }
            }
            if (forceDisplaySubdomain) {
              break
            }
          }
        }
        for (let pageData of this.activated) {
          if (pageData && pageData.children) {
            for (let child of pageData.children) {
              if (child.url) {
                const a = document.createElement('a')
                a.href = child.url
                if (forceDisplaySubdomain) {
                  let subDomain = ''
                  if (a.hostname !== this.journeyOptions.domain) {
                    subDomain = a.hostname.replace('.' + this.journeyOptions.domain, '')
                  }
                  child.subDomain = subDomain
                }

                child.linkHost = getDomainForView(a.host)
                child.linkPath = a.pathname
                child.linkProtocol = a.protocol
                if (this.journeyOptions.matchType === 'exact') {
                  const pathStartIdx = a.href.indexOf(a.host) + a.host.length
                  child.linkPath = a.href.slice(pathStartIdx)
                }
                child.linkPath = decodeURI(child.linkPath)
              }

              if (child.key !== 'NONE|-1') {
                this.shownKey.push(child.key)
              }
            }
          }
        }
      }

      if (this.activated[pageData.seq] &&
          this.activated[pageData.seq].key === pageData.key) {
        if (pageData.children && pageData.children.length) {
          let parentCount = this.ratioOption === 'Stepwise flow' ? pageData.sessCnt : this.journeyData.sessCnt
          pageData.children.forEach((child, idx) => {
            if (this.shownKey.includes(child.key)) {
              child.transition = child.sessCnt / parentCount * 100
              this.valueRange.transition.push(child.transition)
              child.rollback = child.rollCnt ? child.rollCnt / child.sessCnt * 100 : 0
              child.dropOff = child.dropCnt ? child.dropCnt / child.sessCnt * 100 : 0
              if (child.transition < 0.000001) {
                child.transition = 0
              }
              if (child.rollback < 0.000001) {
                child.rollback = 0
              }
              if (child.dropOff < 0.000001) {
                child.dropOff = 0
              }
              if (child.dropOff > 100) {
                child.dropOff = 100
              }
              this.valueRange.rollback.push(child.rollback)
              this.valueRange.dropOff.push(child.dropOff)
              this.valueRange.sessionTime.push(child.sessTime)
              this.valueRange.repeat.push(child.repeat)
              this.makeDisplayData(child)
            }
          })
        }
      }

      if (pageData.seq === 0) {
        this.valueRange.sessionTime.sort((a, b) => a - b)
        this.valueRange.rollback.sort((a, b) => a - b)
        this.valueRange.dropOff.sort((a, b) => a - b)
        this.valueRange.transition.sort((a, b) => a - b)
        this.valueRange.repeat.sort((a, b) => a - b)
      }

      this.partiallyLoading = false
    },
    showMoreSibling (page, parent) {
      if (this.mostOccurred) {
        this.initMostOccurred()
      }
      if (!this.partiallyLoading) {
        this.setJourneyPluck(null)
        const siblingId = `${parent.id}|${page.seq}-${parent.children.length + 1}`

        this.partiallyLoading = true

        this.apiLoading = true
        getData({
          ...this.dataOption,
          treeId: siblingId,
          expansionType: 'sibling'
        }, false)
          .then(result => {
            if (result.tree.key !== '-1' && !result.nodeMap[result.tree.key]) {
              this.partiallyLoading = false
              this.apiLoading = false
              return
            }

            this.nodeMap = { ...this.nodeMap, ...result.nodeMap }
            const subtree = this.applyJourneyNodeMeta({ ...result.tree }, result.nodeMap)
            this.$set(parent.children, parent.children.length, subtree)
            if (result.parentPatch) {
              this.$set(parent, 'hasMoreChildren', result.parentPatch.hasMoreChildren)
            }
            this.makeData(parent)
            this.setActive(page.seq, parent.children[parent.children.length - 1], false, true)

            this.makeDisplayData(this.journeyData)
            this.apiLoading = false
          })
          .catch(() => {
            EventBus.$emit('onError')
            this.apiLoading = false
          })
      }
    },
    setHover (pages) {
      this.hovered = pages
    },
    setActive (seq, page = null, loadMore = false, setSelected = true, urlIds = [], isPaneFit = true, isCurrentActive = false) {
      if (this.mostOccurred && (loadMore || urlIds.length > 0)) {
        this.initMostOccurred()
      }
      if (setSelected) {
        this.gUrlListForHeatmap = this.findNodeListById(null, page.id)
        this.pageDataForHeatmap = { ...page }
      }
      const activatedLength = this.activated.length
      for (let i = activatedLength - 1; i >= seq; i--) {
        this.$delete(this.activated, i)
      }
      this.$set(this.activated, seq, page)
      if (page) {
        if (loadMore) {
          this.setJourneyPluck(null)
          this.setChildren(seq, page, isPaneFit)
          return
        }
        if (setSelected) {
          if (this.selected && this.selected.key === page.key) {
            this.resetActive()
          } else {
            this.selected = page
            if (this.reportShowing) {
              this.hideReport()
            }
          }
        }
        if ((loadMore === false && seq < this.dataOption.limit - 1) ||
            (page.children && page.children.length) ||
            (this.journeyPluck && seq < this.pluckTreeInfo.length)) {
          if (page.children && page.children.length) {
            const nextRank = !this.journeyPluck ? 0 : this.getPluckChildIndex(page, seq + 1)
            if (nextRank > -1 && page.children[nextRank]) {
              this.setActive(seq + 1, page.children[nextRank], false, false, [], isPaneFit)
            }
          }
          if (setSelected) {
            this.makeDisplayData(this.journeyData)
          }
        }
      }
      if (isPaneFit) {
        this.onPaneScaleFit()
      }
    },
    setChildren (seq, page, isPaneFit = false) {
      let target = this.findNodeById(this.journeyData, page.id)
      if (target) {
        this.setJourneyPluck(null)
        this.apiLoading = true
        getData({
          ...this.dataOption,
          treeId: page.id,
          expansionType: 'child'
        }, false)
          .then(result => {
            if (!result.nodeMap[result.tree.key]) {
              this.apiLoading = false
              return
            }

            this.nodeMap = { ...this.nodeMap, ...result.nodeMap }
            const subtree = this.applyJourneyNodeMeta({ ...result.tree }, result.nodeMap)
            target.children = subtree.children || []
            this.$set(target, 'hasMoreChildren', result.tree.hasMoreChildren)
            this.makeData(target)

            if (target.children.length > 0) {
              this.setActive(seq + 1, target.children[0], false, false, [], isPaneFit)
            }
            this.makeDisplayData(this.journeyData)
            this.apiLoading = false
          })
          .catch(() => {
            EventBus.$emit('onError')
            this.apiLoading = false
          })
      }
    },
    findNodeListById (from, id) {
      if (!from) {
        from = this.journeyData
      }
      let pageLocation = this.getNodeLocationById(id)
      const gUrlList = []
      let target = from
      for (let i = 1; i < pageLocation.length; i++) {
        if (target && target.key !== '-1') {
          gUrlList.push(target.key)
        }
        target = target.children[pageLocation[i] - 1]
      }
      return gUrlList
    },
    findNodeById (from, id) {
      if (!from) {
        from = this.journeyData
      }
      let pageLocation = this.getNodeLocationById(id)
      let target = from
      for (let i = 1; i < pageLocation.length; i++) {
        target = target.children[pageLocation[i] - 1]
      }
      return target
    },
    getNodeLocationById (id) {
      let pieces = id.split('|')
      if (!pieces || pieces.length < 1) {
        return []
      }
      let pageLocation = []
      for (let piece of pieces) {
        pageLocation.push(piece.split('-')[1])
      }
      return pageLocation
    },
    resetActive (action = null) {
      this.selected = null
      this.pageDataForHeatmap = null
      if (this.reportShowing) this.hideReport()
      if (this.mostOccurred && action) this.initMostOccurred()
    },
    setRatioOption (value) {
      if (this.ratioOption !== value) {
        this.ratioOption = value
        this.makeDisplayData(this.journeyData)
      }
    },
    setSeeAll (value) {
      if (this.seeAll !== value) {
        this.seeAll = value
      }
    },
    setPluck (value) {
      if (!value) {
        this.setJourneyPluck(null)
        this.mostOccurred = false
        this.mostOccurredValue = null
      }
      if (this.settingPluck !== value) {
        this.settingPluck = value
      }
    },
    setGrouping (value) {
      if (this.grouping !== value) {
        this.grouping = value
      }
      EventBus.$emit('group-state', this.grouping)
    },
    onGroupChange (value) {
      if (this.group !== value) {
        this.group = value
      }
      this.setGrouping(true)
    },
    onDownloadExcel () {
      let fileName = this.journeyOptions.domain + '_Journey'
      const isSampling = this.domain.preSamplingRate < 100 || this.domain.postSamplingRate < 100
      let excel = new Excel()
      this.apiLoading = true
      excel.setReportInfo(this.journeyData, isSampling, this.originJourneyData).then(() => {
        excel.setReportData(this.activated, isSampling, this.originJourneyData)
          .then(() => {
            excel.download(fileName)
            this.apiLoading = false
          })
      }).catch(() => {
        this.apiLoading = false
      })
    },
    showReport (pageData) {
      if (this.mostOccurred) this.initMostOccurred()
      if (pageData.seq > 0 && (!this.reportShowing || this.reportShowing.key !== pageData.key)) {
        this.setActive(pageData.seq, pageData, false, true)
        setTimeout(() => {
          this.reportShowing = pageData
          this.formerPosition = {
            x: this.paneMarginLeft,
            y: this.paneMarginTop
          }
          this.onCenter(pageData.index.x, pageData.index.y, 195, 0)
          setTimeout(() => {
            this.openNodeReport = true
          }, 300)
        }, 100)
      }
    },
    hideReport () {
      this.openNodeReport = false
      setTimeout(() => {
        this.reportShowing = null
        if (this.formerPosition != null) {
          this.paneMarginLeft = this.formerPosition.x
          this.paneMarginTop = this.formerPosition.y
        }
        this.formerPosition = null
      }, 300)
    },
    onCenter (x, y, offsetX = 0, offsetY = 0) {
      if (this.$refs.paneWrap) {
        const paneWidth = this.paneWidth + 1000
        const paneHeight = this.paneHeight + 1000
        const nodeX = this.itemDistance.x * (x - 1) + this.itemDistance.first.x + 500
        const nodeY = this.itemDistance.y * (y - 1) + this.itemDistance.first.y + 500

        this.paneMarginTop = this.paneCenter.y + (paneHeight / 2 - nodeY) * this.paneScale - 20 - offsetY
        this.paneMarginLeft = this.paneCenter.x + (paneWidth / 2 - nodeX) * this.paneScale + 20 - offsetX
      }
    },
    onPaneScaleFit () {
      if (this.$refs.paneWrap) {
        const paneWrapWidth = this.$refs.paneWrap.clientWidth
        const paneWrapHeight = this.$refs.paneWrap.clientHeight

        let widthFit = paneWrapWidth / this.paneWidth
        let heightFit = paneWrapHeight / this.paneHeight
        let scale = 1.0
        if (widthFit > heightFit) {
          scale = heightFit
        } else {
          scale = widthFit
        }
        this.paneScale = scale

        this.paneMarginTop = this.paneCenter.y
        this.paneMarginLeft = this.paneCenter.x
      }
    },
    onCapture (pageData, device) {
      this.capturingDevice = device
      this.capturingPage = pageData
    },
    onCaptureInHeatmap (device) {
      this.capturingDevice = device
    },
    onCaptureProcessing (pageKey) {
      // this.$set(this.captureProcessing, pageKey, true)
      this.captureProcessing = pageKey
    },
    onPluckSharing (value) {
      if (value) {
        this.pluckLoading = true
        this.pluckToBeSharing = value
        EventBus.$emit('journeyPluckChange', { treeId: value })
      } else {
        this.pluckLoading = false
        this.pluckSharing = false
        this.pluckToBeSharing = false
      }
    },
    onPluckLoading (value = true) {
      if (value) {
        this.pluckLoading = true
        EventBus.$emit('journeyPluckChange', { treeId: value })
      } else {
        this.pluckLoading = false
        EventBus.$emit('cancelLoading', true)
      }
    },
    onPluckAlerting (value = true, data) {
      this.pluckAlerting = value
      if (value && data) {
        this.pluckAlert.title = data.title || ''
        this.pluckAlert.description = data.description || ''
        this.pluckAlert.cancel = data.cancel || ''
        this.pluckAlert.submit = data.submit || ''
        this.pluckAlert.onCancel = data.onCancel || (() => {})
        this.pluckAlert.onSubmit = data.onSubmit || (() => {})
      }
      if (!value) {
        this.pluckAlert.onCancel()
      }
    },
    onSettingUtm (force, tag) {
      if (this.utmEnabled && !force) {
        EventBus.$emit('onLoading', true)
        EventBus.$emit('journeyOptionChange', 'utm', null)
      } else {
        this.utmCache = this.utmCache || this.journeyOptions.utm
        this.settingUtm = tag
      }
    },
    onSetUtm (selectedUtm) {
      this.utmCache = selectedUtm
      this.settingUtm = false
      if (JSON.stringify(this.journeyOptions.utm) !== JSON.stringify(selectedUtm)) {
        EventBus.$emit('onLoading', true)
        setTimeout(() => {
          EventBus.$emit('journeyOptionChange', 'utm', selectedUtm)
        }, 300)
      }
    },
    onSettingGroup () {
      this.movingGroup = false
      this.$router.push({ name: 'group', query: { sid: this.sid } })
    },
    updateBookmark (pageData, updatedKey, value) {
      if (this.nodeMap[updatedKey]) {
        this.$set(this.nodeMap[updatedKey], 'bookmark', value)
      }
      if (pageData.key === updatedKey) {
        this.$set(pageData, 'bookmark', value)
      }
      if (pageData.children) {
        pageData.children.forEach((childData) => {
          this.updateBookmark(childData, updatedKey, value)
        })
      }
    },
    updateBaApply (pageData, gUrlId, value) {
      if (this.nodeMap[gUrlId]) {
        this.$set(this.nodeMap[gUrlId], 'baApply', value)
      }
      if (pageData.key === gUrlId) {
        this.$set(pageData, 'baApply', value)
      }
      if (pageData.children) {
        pageData.children.forEach((childData) => {
          this.updateBaApply(childData, gUrlId, value)
        })
      }
    },
    showTrackingLayer (nodeData) {
      this.trackingNodeData = {
        ...nodeData,
        ...(this.trackingData && { trackingData: this.trackingData })
      }
      this.trackingLayerShowing = true
    },
    setTracking (data) {
      if (data.init) {
        this.stopTracking()
      } else {
        this.trackingData = data
        this.trackingLayerShowing = false
        this.loadData()
      }
    },
    stopTracking () {
      this.trackingData = null
      this.trackingNodeData = null
      this.loadData()
    },
    changeHeatmapParams () {

    }
  },
  data () {
    return {
      gUrlListForHeatmap: null,
      pageDataForHeatmap: null,
      heatmapParams: null,
      isDisabled: false,
      loaded: false,
      partiallyLoading: false,
      apiLoading: false,
      movingGroup: false,
      noData: false,
      noResampleData: false,
      seeAll: true,
      capturingPage: null,
      capturingDevice: null,
      ratioOption: 'Stepwise flow',
      defaultThumbnail: null,
      paneScale: 1.0,
      paneMarginTop: 0,
      paneMarginLeft: 0,
      paneDragging: false,
      maxSize: {
        x: 0,
        y: 0
      },
      indexMax: {
        x: 1,
        y: 1
      },
      hovered: null,
      selected: null,
      activated: [],
      shownKey: [],
      journeyData: null,
      journeyDataCache: {},
      formerPosition: null,
      valueRange: {
        transition: [],
        rollback: [],
        dropOff: [],
        sessionTime: [],
        repeat: []
      },
      reportShowing: null,
      captureProcessing: null,
      wholeCaptureProcessing: [],
      captureProcessingTimer: null,
      wholeCaptureProcessingTimer: null,
      openNodeReport: false,

      grouping: false,
      group: '1',
      groupItems: {},

      settingPluck: false,
      pluckToBeSharing: false,
      pluckSharing: false,
      pluckLoading: false,
      paneGeometryHidden: false,
      pluckAlerting: false,
      pluckAlert: {
        title: '',
        description: '',
        cancel: '',
        submit: '',
        onCancel: () => {},
        onSubmit: () => {}
      },
      pluckError: '',

      overallSessionData: {
        sessCnt: 0,
        memberSessCnt: 0
      },
      nodeMap: {},
      coreJourneyRankPath: null,
      trackingData: null,
      trackingLayerShowing: false,
      trackingNodeData: null,

      cancelLoading: false,
      samplingInfo: false,
      isHeatmapView: false,
      targetHeatmapPv: 0,
      targetDeviceCapture: false,
      //  data of most occurred
      mostOccurred: false,
      mostOccurredValue: null,
      mostOccurredLoading: false,
      samplingReliabilityText: null
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-pane {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .ba-pane-control {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 8;
    width: 52px;
    height: 100%;
  }

  .pluck-pane {
    position: absolute;
    top: 20px;
    left: 50%;
    z-index: 9;
    transform: translate(-50%, 0);

    &.pluck-leave-active,
    &.pluck-enter-active {
      transition: opacity 0.5s ease,
      transform 0.5s ease;
      opacity: 1;
      transform: translate(-50%, 0);
    }

    &.pluck-enter,
    &.pluck-leave-to {
      opacity: 0;
      transform: translate(-50%, -50px);
    }
  }

  .grouping-pane {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 11;

    &.grouping-leave-active,
    &.grouping-enter-active {
      transition: right 0.5s ease;
      right: 20px;
    }

    &.grouping-enter,
    &.grouping-leave-to {
      right: -250px;
    }
  }

  .pluck-sharing-container {
    width: 510px;
    height: 340px;
    border-radius: 14px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #fff;
  }

  .pluck-loading-container {
    width: 430px;
    height: 330px;
    border-radius: 14px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #fff;
  }

  .pane-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    background-color: #e5e5e1;
    transition: background-color 0.3s ease;
    overflow: hidden;

    &.is-geometry-hidden {
      .pane {
        opacity: 0;
        pointer-events: none;
        transition: none !important;
      }
    }

    &.disabled {
      background-color: #dcdcd9;

      .pane {
        background-color: #dcdcd9;
      }
    }

    .pane-dimmed {
      background-color: rgba(0,0,0,0.5);
      transition: opacity 0.3s ease;
      width: 100%;
      height: 100%;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 10;
    }

    .pane {
      cursor: grab;
      position: absolute;
      top: 50%;
      left: 50%;
      background-color: #f4f4f1;
      border: 1px dashed #cfcfcf;
      padding: 1000px;
      margin: -1px;
      transition: transform 0.3s ease,
      background-color 0.3s ease,
      margin-top 0.3s ease,
      margin-left 0.3s ease;
      transform-origin: 50% 50%;

      .pane-back {
        position: absolute;
        z-index: -1;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }

      & /deep/ #nodeAll .thumbnail--wrap,
      & /deep/ #nodeAll .item-info {
        cursor: grab;
      }

      & /deep/ .arrow {
        position: absolute;
      }

      &.dragging {
        transition: none;
        cursor: grabbing;

        & /deep/ #nodeAll .thumbnail--wrap {
          cursor: grabbing;
        }
      }

      .nodes {
        position: relative;
        left: 40px;
      }

      .ai-categorize-wrapper {
        position: relative;
        cursor: pointer;
        margin-left: 36px;
        transform: translate(-50%, 0);
        z-index: 9;
        max-width: 170px;
        width: 100%;
        height: 28px;

        .ai-categorize-btn-wrap {
          width: fit-content;
          position: absolute;
          font-size: 12px;
          font-weight: 500;

          .ai-categorize-btn {
            color: #0074FF;
            display: flex;
            align-items: center;
            gap: 4px;
            text-decoration: none;

            .icon--tooltip-link {
              transform: rotate(90deg);
            }

            &:hover {
              text-decoration: underline;
            }
          }
        }

        &.device--tablet {
          margin-left: -33px;
        }

        &.device--phone {
          margin-left: -63px;
        }

        &.device--all {
          margin-left: 0;
        }

        &.disabled {
          opacity: 0.3;
        }
      }
    }
  }

  &.device--tablet {
    .pane {
      .nodes {
        left: 100px;
      }
    }
  }

  &.device--phone {
    .pane {
      .nodes {
        left: 110px;
      }
    }
  }

  .pane-no-data {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    &.fade-leave-active,
    &.fade-enter-active {
      transition: opacity 0.5s ease;
      opacity: 1;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
    }

    h3 {
      font-size: 20px;
      font-weight: 500;
      color: #444444;
      margin: 10px 0 20px 0;
    }

    p {
      font-size: 14px;
      line-height: 1.57;
      color: #666666;
      margin-bottom: 50px;
    }
  }

  .reliability {
    position: absolute;
    z-index: 9;
    bottom: 43.5px;
    right: 25px;
    vertical-align: middle;
    height: 28px;
    border-radius: 5px;

    &.fade-enter-active,
    &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.3s ease;
    }
    &.fade-enter,
    &.fade-leave-to{
      opacity: 0;
    }

    i {
      margin: 4px 0 0 5px;
      float:left;
    }

    span {
      color: #767676;
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      line-height: 28px;
      padding: 0 0px 0 5px;
      vertical-align: middle;
    }
  }
  .pane-base-info {
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    z-index: 8;

    color: #2f2f2f;
    font-weight: 400;
    font-size: 12px;
    line-height: 1;
    &.fade-leave-active,
    &.fade-enter-active {
      transition: opacity 0.5s ease;
      opacity: 1;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
    }

    .sampling-reliability {
      display: flex;
      align-items: center;
      gap: 5px;
      .icon-wrap {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #FFB427;
        box-sizing: border-box;
      }
    }
    .pane-date {
      padding-left: 25px;
    }
  }

  .node-report {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
    width: 444px;
    height: 100%;
    box-shadow: 0 2px 20px -2px rgba(0, 0, 0, 0.08);
    border-left: 1px solid #c9c9c6;
    background-color: #ffffff;
    transition: transform 0.5s ease;

    &.node-report-enter,
    &.node-report-leave-to {
      transform: translate(110%, 0);
    }

    .ba-node-report {
      overflow: auto;
      height: 100%;
      position: relative;
      z-index: 3;
    }

    .close-report {
      position: absolute;
      z-index: 2;
      top: 50%;
      left: -20px;
      width: 20px;
      height: 50px;
      padding: 17px 0;
      border: 1px solid #c9c9c6;
      border-right: none;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
      transform: translateY(-50%);
      background: #ffffff;

      &::after,
      &::before{
        content: "";
        width: 6px;
        height: 6px;
        border: 1px solid #c9c9c6;
        position: absolute;
        right: 0;
        border-left: none;
      }

      &::after{
        border-top: none;
        border-radius: 0 0 5px 0;
        top: -7px;
      }

      &::before{
        border-bottom: none;
        border-radius: 0 5px 0 0;
        bottom: -7px;
      }

      .icon {
        &::after,
        &::before{
          content: "";
          width: 6px;
          height: 6px;
          border: 2px solid #ffffff;
          position: absolute;
          right: -2px;
          border-left: none;
        }

        &::after{
          border-top: none;
          border-radius: 0 0 7px 0;
          top: -6px;
        }

        &::before{
          border-bottom: none;
          border-radius: 0 7px 0 0;
          bottom: -6px;
        }
      }
    }
  }

  .partiallyLoading {
    &.fade-leave-active,
    &.fade-enter-active {
      transition: opacity 0.3s ease;
      opacity: 1;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
    }
  }
  .banner-most-occurred-container {
    position: fixed;
    width: 100%;
    min-width: 1280px;
    top: 0;
    z-index: 9999;
  }
  .banner-most-occurred-wrapper {
    cursor: pointer;
    position: absolute;
    width: calc(100% - 303px);
    height: 147px;
    top: 0;
    right: 0;
    padding: 22px 28px;
    background: linear-gradient(103deg, #6628CD 47.27%, #7927CC 69.95%);
    box-shadow: 0px 9px 23px rgba(0, 0, 0, 0.25);
    margin: 6px 6px 20px;
    box-sizing: border-box;
    border-radius: 8px;
    transition: transform 600ms $ease-out-cubic;
    transition-delay: 300ms;
    transform: translateY(-300px);
    .banner-img {
      position: absolute;
      top: 0;
      right: 150px;
    }
    .banner-contents {
      .title {
        color: #FFF;
        font-size: 30px;
        font-style: normal;
        font-weight: 700;
        line-height: 160%; /* 48px */
        margin-bottom: 8px;
        &::v-deep span {
          color: #F9E000;
        }
      }
      .description {
        position: relative;
        display: inline-block;
        .banner-click-btn {
          position: absolute;
          bottom: 0;
          left: calc(100% + 10px);
          width: 25px;
          height: 25px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, .06);
          border: 1px solid rgba(255, 255, 255, .25);
          padding: 6px 5px;
          box-sizing: border-box;
          i {
            transform: rotate(270deg);
          }
          &:hover {
            background-color: rgba(255, 255, 255, .16);
          }
        }
      }
      .description p {
        color: #D2B9FB;
        font-size: 14px;
        font-weight: 400;
        line-height: 22px; /* 157.143% */
      }
    }
    .banner-close {
      position: absolute;
      top:16px;
      right: 16px;
    }
    .banner-logo {
      display: flex;
      align-items: center;
      column-gap: 7px;
      position: absolute;
      right: 16px;
      bottom: 16px;
      .text {
        color: #fff;
        font-size: 13px;
        font-weight: bold;
      }
      .icon--beta {
        position: absolute;
        bottom: 100%;
        right:0;
      }
    }
    &.show {
      transform: translateY(0);
    }
  }
}
.most-occurred-loading {
  position: absolute;
  z-index: 9999;
  inset: 0;
  background-color: rgba(0,0,0,0.6);
  & > div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
@keyframes dropdown {
  0% {
    transform: translateY(-100%);
  }

  16% {
    transform: translateY(32.27%);
  }

  28% {
    transform: translateY(-13.12%);
  }

  44% {
    transform: translateY(4.63%);
  }

  59% {
    transform: translateY(-1.64%);
  }

  73% {
    transform: translateY(0.58%);
  }

  88% {
    transform: translateY(-0.2%);
  }

  100% {
    transform: translateY(0%);
  }
}
@keyframes billow {
  0% {
    transform: scale(0);
  }
  // 28% {
  //   transform: scale(1.1);
  // }
  // 59% {
  //   transform: scale(0.98)
  // }
  95% {
    transform: scale(1.05)
  }
  100% {
    transform: scale(1);
  }
}

.slide-fade-leave-active {
  transition: all 250ms cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-fade-enter-active {
  transition: all 250ms ease;
}
.slide-fade-enter, .slide-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, 50px);
  }
</style>

<style>
body {
  overscroll-behavior-x: contain;
}
</style>
